import BootCheckbox from "components/common/custom/forms/BootCheckbox";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import FalconCloseButton from "components/common/FalconCloseButton";
import React, { useEffect } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { handleFillStepData } from "features/cliams/config/claimSlice";

const EditPatientInfoModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const {
    genderList,
    maritalList,
    professionList,
    nationalityList,
    relationshipList,
    religionList,
    identifierTypeList,
  } = useSelector((state) => state.eligibilityCheck);
  const { stepFormData } = useSelector((state) => state.claim);

  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    control,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: stepFormData || {},
  });

  useEffect(() => {
    reset(stepFormData || {});
  }, [stepFormData, reset]);

  const handleFormSubmit = (data) => {
    clearErrors("id");

    const idValue = data.id;
    const identifierTypeId = data.identifier_type?.ident_type_id;
    const firstChar = idValue?.charAt(0);

    let hasError = false;

    if (!idValue) {
      setError("id", {
        type: "manual",
        message: "Please enter ID or Iqama No.",
      });
      hasError = true;
    } else if (identifierTypeId === 1 && !["2"].includes(firstChar)) {
      setError("id", {
        type: "manual",
        message: "Iqama must start with  2",
      });
      hasError = true;
    } else if (identifierTypeId === 6 && !["1"].includes(firstChar)) {
      setError("id", {
        type: "manual",
        message: "ID must start with  1",
      });
      hasError = true;
    } else if (
      (identifierTypeId === 1 || identifierTypeId === 6) &&
      idValue?.length !== 10
    ) {
      setError("id", {
        type: "manual",
        message: "ID/Iqama must be 10 digits",
      });
      hasError = true;
    } else if (identifierTypeId !== 1 && ["2"].includes(firstChar)) {
      setError("id", {
        type: "manual",
        message:
          "Identifier type format doesn't match (OnlyIqama should start with 2)",
      });
      hasError = true;
    } else if (identifierTypeId !== 6 && ["1"].includes(firstChar)) {
      setError("id", {
        type: "manual",
        message:
          "Identifier type format doesn't match (Only National ID should start with 1)",
      });
      hasError = true;
    }

    const isChildRelationShip =
      data.relationship?.patrtl_name?.toLowerCase() === "child";
    const isNewBorn = data.new_born;
    const childID = data.child_id;
    const childType = data.child_id_type?.ident_type_id;
    const firstCharChild = childID?.charAt(0);

    if (isChildRelationShip && !isNewBorn) {
      if (!childID) {
        setError("child_id", {
          type: "manual",
          message: "Please enter relation ID or relation Iqama No.",
        });
        hasError = true;
      } else if (childType === 1 && firstCharChild !== "2") {
        setError("child_id", {
          type: "manual",
          message: "Iqama No. must start with 2",
        });
        hasError = true;
      } else if (childType === 6 && firstCharChild !== "1") {
        setError("child_id", {
          type: "manual",
          message: "National ID No. must start with 1",
        });
        hasError = true;
      } else if (
        (childType === 1 || childType === 6) &&
        childID?.length !== 10
      ) {
        setError("child_id", {
          type: "manual",
          message: "Relation ID/Iqama must be 10 digits",
        });
        hasError = true;
      } else if (childType !== 1 && ["2"].includes(firstCharChild)) {
        setError("child_id", {
          type: "manual",
          message: "ID format does not match: Only Iqama should start with 2",
        });
        hasError = true;
      } else if (childType !== 6 && ["1"].includes(firstCharChild)) {
        setError("child_id", {
          type: "manual",
          message:
            "ID format does not match: Only National ID should start with 1",
        });
        hasError = true;
      }
    }

    if (isNewBorn) {
      if (!childID) {
        setError("child_id", {
          type: "manual",
          message: "Please enter relation ID or relation Iqama No.",
        });
        hasError = true;
      } else if (childType === 1 && firstCharChild !== "2") {
        setError("child_id", {
          type: "manual",
          message: "Iqama No. must start with 2",
        });
        hasError = true;
      } else if (childType === 6 && firstCharChild !== "1") {
        setError("child_id", {
          type: "manual",
          message: "National ID No. must start with 1",
        });
        hasError = true;
      } else if (
        (childType === 1 || childType === 6) &&
        childID?.length !== 10
      ) {
        setError("child_id", {
          type: "manual",
          message: "Relation ID/Iqama must be 10 digits",
        });
        hasError = true;
      } else if (childType !== 1 && ["2"].includes(firstCharChild)) {
        setError("child_id", {
          type: "manual",
          message: "ID format does not match: Only Iqama should start with 2",
        });
        hasError = true;
      } else if (childType !== 6 && ["1"].includes(firstCharChild)) {
        setError("child_id", {
          type: "manual",
          message:
            "ID format does not match: Only National ID should start with 1",
        });
        hasError = true;
      }
    }

    if (hasError) return;

    // Dispatch one by one
    dispatch(
      handleFillStepData({ check: "first_name", value: data?.first_name })
    );
    dispatch(
      handleFillStepData({ check: "family_name", value: data?.family_name })
    );
    dispatch(
      handleFillStepData({
        check: "identifier_type",
        value: data?.identifier_type,
      })
    );
    dispatch(handleFillStepData({ check: "id", value: data?.id }));
    dispatch(
      handleFillStepData({ check: "patient_mrn", value: data?.patient_mrn })
    );
    dispatch(
      handleFillStepData({ check: "membership_no", value: data?.membership_no })
    );
    dispatch(
      handleFillStepData({ check: "policy_no", value: data?.policy_no })
    );
    dispatch(
      handleFillStepData({
        check: "policy_holder_name",
        value: data?.policy_holder_name,
      })
    );
    dispatch(
      handleFillStepData({ check: "class_name", value: data?.class_name })
    );
    dispatch(
      handleFillStepData({
        check: "birth_date",
        value:
          data.birth_date instanceof Date
            ? data.birth_date.toISOString()
            : data.birth_date,
      })
    );
    dispatch(
      handleFillStepData({ check: "mobile_no", value: data?.mobile_no })
    );
    dispatch(handleFillStepData({ check: "gender", value: data?.gender }));
    dispatch(
      handleFillStepData({
        check: "marital_status",
        value: data?.marital_status,
      })
    );
    dispatch(
      handleFillStepData({ check: "occupation", value: data?.occupation })
    );
    dispatch(handleFillStepData({ check: "religion", value: data?.religion }));
    dispatch(handleFillStepData({ check: "new_born", value: data?.new_born }));

    if (data.new_born || isChildRelationShip) {
      dispatch(
        handleFillStepData({ check: "child_mrn", value: data?.child_mrn })
      );
      dispatch(
        handleFillStepData({
          check: "child_first_name",
          value: data?.child_first_name,
        })
      );
      dispatch(
        handleFillStepData({
          check: "child_family_name",
          value: data?.child_family_name,
        })
      );
      dispatch(
        handleFillStepData({ check: "child_gender", value: data?.child_gender })
      );
      dispatch(
        handleFillStepData({
          check: "child_birth_date",
          value:
            data.child_birth_date instanceof Date
              ? data.child_birth_date.toISOString()
              : data.child_birth_date,
        })
      );
      dispatch(
        handleFillStepData({
          check: "child_id_type",
          value: data?.child_id_type,
        })
      );
      dispatch(
        handleFillStepData({ check: "child_id", value: data?.child_id })
      );
    }

    reset(); // reset the form state
    onHide(); // close modal
  };

  return (
    <Modal show={show} size="xl" centered>
      <Modal.Header>
        <Modal.Title className="fs-10">Patient Information</Modal.Title>
        <FalconCloseButton
          onClick={() => {
            reset(stepFormData || {});
            onHide();
          }}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <Row>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="first_name"
                label="Patient Name"
                placeholder="Patient Name"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Patient Name is required",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="family_name"
                label="Family Name"
                placeholder="Family Name"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Family Name is required",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Controller
                name="identifier_type"
                control={control}
                rules={{ required: "Identifier Type is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="form-label">
                      Identifier Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      value={field.value?.ident_type_id || ""}
                      onChange={(e) => {
                        const selectedValue = identifierTypeList.find(
                          (item) =>
                            item.ident_type_id === parseInt(e.target.value)
                        );
                        setValue("identifier_type", selectedValue || null);
                        clearErrors("id");
                      }}
                    >
                      <option value="">Select Identifier Type</option>
                      {identifierTypeList?.map((item) => (
                        <option
                          key={item.ident_type_id}
                          value={item.ident_type_id}
                        >
                          {item.ident_type_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="id"
                label="Identifier ID"
                placeholder="Identifier ID"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Identifier ID is required",
                  onChange: () => clearErrors("id"),
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="patient_mrn"
                label="MRN"
                placeholder="MRN"
                control={control}
                size="sm"
                required={false}
                errors={errors}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="membership_no"
                label="Membership No"
                placeholder="Membership No"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Membership No is required",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="policy_no"
                label="Policy No"
                placeholder="Policy No"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Policy No is required",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="policy_holder_name"
                label="Policy Holder Name"
                placeholder="Policy Holder Name"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Policy Holder Name is required",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="class_name"
                label="Class"
                placeholder="Class"
                control={control}
                size="sm"
                required={true}
                errors={errors}
                rules={{
                  required: "Class is required",
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <BootDatePicker
                name="birth_date"
                label="Birth Date"
                size="sm"
                control={control}
                rules={{ required: "Birth date is required" }}
                errors={errors}
                selected={
                  watch("birth_date") ? new Date(watch("birth_date")) : null
                }
                onChange={(selected) => setValue("birth_date", selected)}
                placeholder="dd/mm/yyyy"
                maxDate={new Date()}
                isRequired={true}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <TextInput
                name="mobile_no"
                size="sm"
                label="Mobile No."
                control={control}
                placeholder="05xxxxxxxx"
                type="tel"
                inputMode="numeric"
                required={false}
                rules={{
                  required: false,
                  validate: (value) => {
                    if (!value) return true;
                    const isValidFormat = /^05\d{8}$/.test(value);
                    if (!isValidFormat) {
                      return "Mobile number must start with 05 and be 10 digits long";
                    }
                    return true;
                  },
                }}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: "Gender is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="form-label">
                      Gender <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      value={field.value?.genders_code || ""}
                      onChange={(e) =>
                        setValue(
                          "gender",
                          genderList.find(
                            (item) => item.genders_code === e.target.value
                          ) || null
                        )
                      }
                    >
                      <option value="">Select Gender</option>
                      {genderList?.map((item) => (
                        <option
                          key={item.genders_code}
                          value={item.genders_code}
                        >
                          {item.genders_disply}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.gender && (
                      <Form.Text className="text-danger">
                        {errors.gender.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Controller
                name="relationship"
                control={control}
                rules={{ required: "RelationShip is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="form-label">
                      RelationShip <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      value={field.value?.patrtl_code || ""}
                      onChange={(e) =>
                        setValue(
                          "relationship",
                          relationshipList.find(
                            (item) => item.patrtl_code === e.target.value
                          ) || null
                        )
                      }
                    >
                      <option value="">Select Relationship</option>
                      {relationshipList?.map((item) => (
                        <option key={item.patrtl_code} value={item.patrtl_code}>
                          {item.patrtl_name}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.relationship && (
                      <Form.Text className="text-danger">
                        {errors.relationship.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Controller
                name="marital_status"
                control={control}
                rules={{ required: "Marital Status is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="form-label">
                      Marital Status <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      value={field.value?.maritalstatus_code || ""}
                      onChange={(e) =>
                        setValue(
                          "marital_status",
                          maritalList.find(
                            (item) => item.maritalstatus_code === e.target.value
                          ) || null
                        )
                      }
                    >
                      <option value="">Select Marital Status</option>
                      {maritalList?.map((item) => (
                        <option
                          key={item.maritalstatus_code}
                          value={item.maritalstatus_code}
                        >
                          {item.maritalstatus_disply}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.marital_status && (
                      <Form.Text className="text-danger">
                        {errors.marital_status.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Controller
                name="occupation"
                control={control}
                rules={{ required: "Occupation is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="form-label">
                      Occupation <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      value={field.value?.occup_code || ""}
                      onChange={(e) =>
                        setValue(
                          "occupation",
                          professionList.find(
                            (item) => item.occup_code === e.target.value
                          ) || null
                        )
                      }
                    >
                      <option value="">Select Occupation</option>
                      {professionList?.map((item) => (
                        <option key={item.occup_code} value={item.occup_code}>
                          {item.occup_disply}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.occupation && (
                      <Form.Text className="text-danger">
                        {errors.occupation.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Controller
                name="religion"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="form-label">Religion</Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      value={field.value?.religion_code || ""}
                      onChange={(e) =>
                        setValue(
                          "religion",
                          religionList.find(
                            (item) => item.religion_code === e.target.value
                          ) || null
                        )
                      }
                    >
                      <option value="">Select Religion</option>
                      {religionList?.map((item) => (
                        <option
                          key={item.religion_code}
                          value={item.religion_code}
                        >
                          {item.religion_name}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.religion && (
                      <Form.Text className="text-danger">
                        {errors.religion.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              />
            </Col>
            <Col xs={2} className="mt-1">
              <OverlayTrigger
                className="bg-danger"
                placement="bottom"
                flip
                overlay={
                  <Tooltip className="bg-danger rounded text-danger">
                    Only enable for female gender
                  </Tooltip>
                }
              >
                <div>
                  <BootCheckbox
                    label="New Born"
                    name="new_born"
                    control={control}
                    rules={{ required: false }}
                    disabled={watch("gender")?.genders_code !== "female"}
                    size="lg"
                  />
                </div>
              </OverlayTrigger>
            </Col>
            {watch("new_born") && (
              <motion.div
                style={{ border: "2px solid #d9f8eb" }}
                className="rounded p-3 position-relative mt-2"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.5 }}
              >
                <div className="position-absolute top-0 start-50 translate-middle-y px-2">
                  <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
                    <span className="fs-11">New Born Info</span>
                  </span>
                </div>
                <Row className="align-items-end g-3 pt-3">
                  <Col xs={12} sm={6} md={3}>
                    <TextInput
                      errors={errors}
                      name="child_mrn"
                      label="MRN"
                      control={control}
                      size="sm"
                      placeholder="MRN"
                      required={watch("new_born")}
                      rules={{
                        required: watch("new_born") && "MRN is required",
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Controller
                      name="child_id_type"
                      control={control}
                      rules={{ required: "Identifier Type is required" }}
                      render={({ field }) => (
                        <Form.Group>
                          <Form.Label className="form-label">
                            Identifier Type{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            {...field}
                            size="sm"
                            value={field.value?.ident_type_id || ""}
                            onChange={(e) => {
                              const selectedValue = identifierTypeList.find(
                                (item) =>
                                  item.ident_type_id ===
                                  parseInt(e.target.value)
                              );
                              setValue("child_id_type", selectedValue || null);
                              clearErrors("child_id");
                            }}
                          >
                            <option value="">Select Identifier Type</option>
                            {identifierTypeList?.map((item) => (
                              <option
                                key={item.ident_type_id}
                                value={item.ident_type_id}
                              >
                                {item.ident_type_name}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.child_id_type && (
                            <Form.Text className="text-danger">
                              {errors.child_id_type.message}
                            </Form.Text>
                          )}
                        </Form.Group>
                      )}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <TextInput
                      name="child_id"
                      size="sm"
                      label="ID"
                      required={true}
                      control={control}
                      type="text"
                      placeholder="Identifier ID"
                      inputMode="text"
                      errors={errors}
                      rules={{
                        required: "ID is required",
                        onChange: () => clearErrors("child_id"),
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <TextInput
                      name="child_first_name"
                      label="First Name"
                      placeholder="First Name"
                      control={control}
                      size="sm"
                      errors={errors}
                      required={watch("new_born")}
                      rules={{
                        required: watch("new_born") && "First Name is required",
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <TextInput
                      name="child_family_name"
                      label="Family Name"
                      placeholder="Family Name"
                      control={control}
                      size="sm"
                      required={true}
                      errors={errors}
                      rules={{
                        required: "Family Name is required",
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Controller
                      name="child_gender"
                      control={control}
                      rules={{
                        required: watch("new_born") && "Gender is required",
                      }}
                      render={({ field }) => (
                        <Form.Group>
                          <Form.Label className="form-label">
                            Gender <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            {...field}
                            size="sm"
                            value={field.value?.genders_code || ""}
                            onChange={(e) =>
                              setValue(
                                "child_gender",
                                genderList.find(
                                  (item) => item.genders_code === e.target.value
                                ) || null
                              )
                            }
                          >
                            <option value="">Select Gender</option>
                            {genderList?.map((item) => (
                              <option
                                key={item.genders_code}
                                value={item.genders_code}
                              >
                                {item.genders_disply}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.child_gender && (
                            <Form.Text className="text-danger">
                              {errors.child_gender.message}
                            </Form.Text>
                          )}
                        </Form.Group>
                      )}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <BootDatePicker
                      name="child_birth_date"
                      label="Birth Date"
                      control={control}
                      rules={{
                        required: watch("new_born") && "Birth date is required",
                      }}
                      errors={errors}
                      isRequired={watch("new_born")}
                      placeholder="dd/mm/yyyy"
                      selected={
                        watch("child_birth_date")
                          ? new Date(watch("child_birth_date"))
                          : null
                      }
                      minDate={new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)}
                      maxDate={new Date()}
                    />
                  </Col>
                </Row>
              </motion.div>
            )}

            {!watch("new_born") &&
              watch("relationship")?.patrtl_code == "child" && (
                <motion.div
                  style={{ border: "2px solid #d9f8eb" }}
                  className="rounded p-3 position-relative mt-2"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 50 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="position-absolute top-0 start-50 translate-middle-y px-2">
                    <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
                      <span className="fs-11">Beneficiary Info</span>
                    </span>
                  </div>
                  <Row className="align-items-end g-3 pt-3">
                    <Col xs={12} sm={6} md={3}>
                      <TextInput
                        errors={errors}
                        name="child_mrn"
                        label="MRN"
                        control={control}
                        size="sm"
                        placeholder="MRN"
                        required={watch("new_born")}
                        rules={{
                          required: watch("new_born") && "MRN is required",
                        }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <Controller
                        name="child_id_type"
                        control={control}
                        rules={{ required: "Identifier Type is required" }}
                        render={({ field }) => (
                          <Form.Group>
                            <Form.Label className="form-label">
                              Identifier Type{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                              {...field}
                              size="sm"
                              value={field.value?.ident_type_id || ""}
                              onChange={(e) => {
                                const selectedValue = identifierTypeList.find(
                                  (item) =>
                                    item.ident_type_id ===
                                    parseInt(e.target.value)
                                );
                                setValue(
                                  "child_id_type",
                                  selectedValue || null
                                );
                                clearErrors("child_id");
                              }}
                            >
                              <option value="">Select Identifier Type</option>
                              {identifierTypeList?.map((item) => (
                                <option
                                  key={item.ident_type_id}
                                  value={item.ident_type_id}
                                >
                                  {item.ident_type_name}
                                </option>
                              ))}
                            </Form.Select>
                            {errors.child_id_type && (
                              <Form.Text className="text-danger">
                                {errors.child_id_type.message}
                              </Form.Text>
                            )}
                          </Form.Group>
                        )}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <TextInput
                        name="child_id"
                        size="sm"
                        label="ID"
                        required={true}
                        control={control}
                        type="text"
                        placeholder="Identifier ID"
                        inputMode="text"
                        errors={errors}
                        rules={{
                          required: "ID is required",
                          onChange: () => clearErrors("child_id"),
                        }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <TextInput
                        name="child_first_name"
                        label="First Name"
                        placeholder="First Name"
                        control={control}
                        size="sm"
                        errors={errors}
                        required={watch("new_born")}
                        rules={{
                          required:
                            watch("new_born") && "First Name is required",
                        }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <TextInput
                        name="child_family_name"
                        label="Family Name"
                        placeholder="Family Name"
                        control={control}
                        size="sm"
                        required={true}
                        errors={errors}
                        rules={{
                          required: "Family Name is required",
                        }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <Controller
                        name="child_gender"
                        control={control}
                        rules={{
                          required: watch("new_born") && "Gender is required",
                        }}
                        render={({ field }) => (
                          <Form.Group>
                            <Form.Label className="form-label">
                              Gender <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                              {...field}
                              size="sm"
                              value={field.value?.genders_code || ""}
                              onChange={(e) =>
                                setValue(
                                  "child_gender",
                                  genderList.find(
                                    (item) =>
                                      item.genders_code === e.target.value
                                  ) || null
                                )
                              }
                            >
                              <option value="">Select Gender</option>
                              {genderList?.map((item) => (
                                <option
                                  key={item.genders_code}
                                  value={item.genders_code}
                                >
                                  {item.genders_disply}
                                </option>
                              ))}
                            </Form.Select>
                            {errors.child_gender && (
                              <Form.Text className="text-danger">
                                {errors.child_gender.message}
                              </Form.Text>
                            )}
                          </Form.Group>
                        )}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <BootDatePicker
                        name="child_birth_date"
                        label="Birth Date"
                        control={control}
                        rules={{
                          required:
                            watch("new_born") && "Birth date is required",
                        }}
                        errors={errors}
                        isRequired={watch("new_born")}
                        placeholder="dd/mm/yyyy"
                        selected={
                          watch("child_birth_date")
                            ? new Date(watch("child_birth_date"))
                            : null
                        }
                        minDate={
                          new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
                        }
                        maxDate={new Date()}
                      />
                    </Col>
                  </Row>
                </motion.div>
              )}
          </Row>
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                reset(stepFormData || {});
                onHide();
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditPatientInfoModal;
